import {PrimitiveType} from 'react-intl'

import {Offer} from '@vio/core/src/offer/types/offer'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

import {PriceTrendMessageKey} from './utils/messages'

/**
 * Union type for offers used by PriceTrend.
 * Supports two offer types:
 * - Offer: Regular room offer from Rooms API (BOFH)
 * - SearchOffer: Top offer from Search API (can be BOFH or redirect)
 * Note: SplitBooking is not supported - split booking offers are filtered out
 * and the component will not render when a split booking is selected.
 */
export type PriceTrendOffer = Offer | SearchOffer

/**
 * Type guard to check if an offer is a room Offer (has id and prices array)
 * Room offers are from the Rooms API and are always BOFH.
 */
export const isRoomOffer = (
  offer: PriceTrendOffer | undefined
): offer is Offer => {
  return (
    offer !== undefined &&
    'id' in offer &&
    'prices' in offer &&
    Array.isArray(offer.prices)
  )
}

/**
 * Type guard to check if an offer is a SearchOffer (has rate property)
 * SearchOffers can be either BOFH (intermediaryProvider: 'FHT') or redirect.
 */
export const isSearchOffer = (
  offer: PriceTrendOffer | undefined
): offer is SearchOffer => {
  return offer !== undefined && 'rate' in offer && !('prices' in offer)
}

export interface PriceTrendInsightsPayload {
  medianHotelPrice: number
  timeRangeDays: number
  lastMedianSimilarHotelsPricePoint: number | null
  hotelChangePercentageThreshold: number
  marketChangePercentageThreshold: number
  marketPriceTrendPercentage: number | null
}

export interface MessageConfig {
  messageKey: PriceTrendMessageKey
  values?: Record<string, PrimitiveType>
}

export type PriceTrendIconKey =
  | 'hotelUp'
  | 'hotelDown'
  | 'hotelStable'
  | 'similarUp'
  | 'similarDown'

export interface PriceTrendInfoContent {
  title: MessageConfig
  subtitle: MessageConfig
  tooltip: MessageConfig
  icon: PriceTrendIconKey
}

export interface PriceTrendContent {
  isPriceDropped: boolean
  recommendation: MessageConfig
  hotelChange: PriceTrendInfoContent
  similarHotels?: PriceTrendInfoContent
}

export interface PriceTrendsTrackingContext {
  [key: string]: unknown
  priceInsightsId: string
  insightType: string | null
  actionType: string | null
  apLayout: string
  isSimilarHotelsAvailable: boolean
}

export type HotelTrendDirection = 'up' | 'down' | 'stable'
export type SimilarHotelsDirection = 'cheaper' | 'expensive' | 'same'

export interface PriceTrendProps {
  /** The hotel ID to fetch price trends for */
  hotelId: string
  /** Insights data used to display the price trend. We use this to facilitate storybook stories.*/
  insights?: PriceTrendInsightsPayload
  /** Whether offers are fully loaded. Component will only show after offers are complete. */
  areOffersLoaded?: boolean
  /** Callback fired when the price trend button is clicked. Parent component should handle navigation. */
  onPriceTrendButtonClick?: (messageKey?: PriceTrendMessageKey) => void
}

export interface PriceTrendMobileProps extends PriceTrendProps {
  /**
   * Hide the dark-purple banner inside the compact card. Used by the
   * after-OfferBox placement where the OfferBox already owns its own
   * `PriceInsightsBanner`. The card renders just the PriceRangeBar + CTA.
   */
  hideCompactBanner?: boolean
}

export type PriceTrendDesktopProps = PriceTrendProps
